<template>
  <div class="space-center login">
    <!-- 标题 -->
    <h2 class="login-title">登录</h2>

    <!-- 登录表单 -->
    <div class="form">
      <!-- 账号输入框 -->
      <div class="input-group" :class="{ 'has-error': accountError }">
        <input ref="accountInputRef" v-model="formData.account" type="text" autofocus placeholder="请输入账号/手机号"
          class="main-input" @input="accountError = ''" />
        <div class="svg">
          <svg-icon name="用户" color="svg" />
        </div>
        <div v-if="accountError" class="error-message">
          {{ accountError }}
        </div>
      </div>

      <!-- 密码输入框 -->
      <div class="input-group password-group" :class="{ 'has-error': passwordError }">
        <input ref="passwordInputRef" v-model="formData.password" :type="showPassword ? 'text' : 'password'"
          placeholder="请输入密码" class="main-input" @input="passwordError = ''" />
        <div class="svg password-toggle" @click="togglePassword">
          <svg-icon :name="showPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="passwordError" class="error-message">
          {{ passwordError }}
        </div>
      </div>

      <!-- 图形验证码（登录失败后显示）-->
      <div v-if="showCaptcha" class="input-group captcha-group" :class="{ 'has-error': captchaError }">
        <input ref="captchaInputRef" v-model="formData.captcha" type="text" placeholder="请输入图形验证码" class="main-input"
          maxlength="6" @input="onCaptchaInput" @compositionstart="onCaptchaCompositionStart"
          @compositionend="onCaptchaCompositionEnd" @blur="onCaptchaBlur" @change="onCaptchaBlur" />
        <div class="captcha-img" @click="refreshCaptcha" title="点击刷新">
          <img v-if="captchaImgUrl" :src="captchaImgUrl" alt="验证码" />
          <div v-else class="captcha-placeholder">加载中...</div>
        </div>
        <div v-if="captchaError" class="error-message">
          {{ captchaError }}
        </div>
      </div>

       <!-- 自动登陆功能需调整  -->
      <div class="group-password">
        <label class="remember-checkbox">
          <input type="checkbox" v-model="rememberAccount" />
          <span class="checkmark"></span>
          <span class="label-text">自动登陆</span>
        </label>
        <div class="forgot-password" @click="goToForgotPassword">
          忘记密码？
        </div>
      </div>

      <!-- 登录按钮 -->
      <button class="Signup-Btn login-button" :disabled="loading" @click="handleSubmit">
        <svg-icon v-if="loading" class="icon-loading" size="18" name="加载" color="#fff" />
        <span class="button-text">{{ loading ? '登录中...' : '登录' }}</span>
      </button>

      <!-- 用户协议 -->
      <!-- 底部链接：服务设置和注册账号 -->
      <div class="register-link">
        <span class="link-text server-setting" ></span>
                <!-- <span class="link-text server-setting" @click="showServerModal = true">服务器设置</span> -->

        <span class="link-text" @click="goToRegister">注册账号</span>
      </div>
    </div>
    
    <!-- Loading -->
         <MLoading :config="mLoadingConfig" />
    <!-- 服务器配置弹窗 -->
    <ServerConfigModal v-model:visible="showServerModal" @confirm="handleServerConfigured" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useBriefStore, useSettingStore, useUserStore } from 'd3components'
import { toast, isMobile } from 'd3components/src/utils/tools'
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { useI18n } from 'vue-i18n'
import { i18n } from '@/i18n'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'
import { addDynamicRoutes } from '@/router'
import { detectPlatform } from '@/utils/platform'
import { getLuatCaptcha } from '../api'
import ServerConfigModal from '@/components/ServerConfigModal.vue'
import { isMiniProgram } from 'd3components/src/utils/tools'
import MLoading from 'd3components/src/components/item-d3/third/d3_toast.vue';
import mitt from 'mitt-utils';
import { getJumpviewRouteLocation } from '@/utils/jumpview';
// import { getPid } from 'd3components/src/components/actionUtils/actionUtil';
import { getPid } from 'exeAction-utils'

defineOptions({ name: 'luatos-login' })
// 接收父组件传递的 agreeTerms 属性
const props = defineProps<{
  agreeTerms?: boolean,
  updateAgreeTerms:(value:boolean)=>void
}>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n({ useScope: 'global' })
const userStore = useUserStore()
const settingStore: any = useSettingStore()
const briefStore: any = useBriefStore

// ===== 输入框引用 =====
const accountInputRef = ref<HTMLInputElement | null>(null)
const passwordInputRef = ref<HTMLInputElement | null>(null)
const captchaInputRef = ref<HTMLInputElement | null>(null)

// ===== 状态管理 =====
const loading = ref(false)
const rememberAccount = ref(false)
const showPassword = ref(false)
const isCaptchaComposing = ref(false)
const showCaptcha = ref(false) // 是否显示图形验证码
const captchaImgUrl = ref('') // 图形验证码图片（提前加载）
const captchaId = ref('') // 图形验证码ID
const loginFailCount = ref(0) // 登录失败次数
const showServerModal = ref(false) // 服务器配置弹窗
const agreeUsers = ref<Record<string, boolean>>({});

// 错误状态
const accountError = ref('')
const passwordError = ref('')
const captchaError = ref('')
// 登录页专用的 loading 遮罩配置
const pid = getPid(route);
const mLoadingDid = 'login_m_loading';
const mLoadingMittName = `${pid}_mitt_did_${mLoadingDid}`;
const mLoadingConfig = { did: mLoadingDid, pid };
const openMLoading = async () => {
  await nextTick();
  console.log('login emit open ->', mLoadingMittName);
  mitt.emit(mLoadingMittName, { flg: 'f_open' });
};

const closeMLoading = () => {
  console.log('login emit close ->', mLoadingMittName);
  mitt.emit(mLoadingMittName, { flg: 'f_close' });
};
// 表单数据
const formData = ref({
  account: '',
  password: '',
  captcha: '', // 图形验证码
})

const syncInputValues = () => {
  const account = accountInputRef.value?.value
  const password = passwordInputRef.value?.value
  const captcha = captchaInputRef.value?.value
  if (typeof account === 'string') {
    formData.value.account = account
  }
  if (typeof password === 'string') {
    formData.value.password = password
  }
  if (typeof captcha === 'string') {
    formData.value.captcha = captcha
  }
}

const getLoginHistory = () => getsubKey('mobile', 'user_info') as any[];
// ===== 业务逻辑 =====

const togglePassword = () => {
  showPassword.value = !showPassword.value
}


const goToForgotPassword = () => {
  // router.push('/forgotPassword')
  router.push({ path: '/forgotPassword', })

  // route
}


const goToRegister = () => {
  router.push({ path: '/register' })

}


const refreshCaptcha = async () => {
  try {
    const res = await getLuatCaptcha()
    if (res.code==0) {
      captchaImgUrl.value = res.value.img
      captchaId.value = res.value.captcha_id;
    } else {
      toast(res?.msg || '获取验证码失败', 3)
    }
  } catch (err: any) {
    toast(err?.message || '获取验证码异常', 3)
  }
}


const validateAccount = () => {
  if (!formData.value.account) {
    accountError.value = t('login.phoneRequired')
    nextTick(() => accountInputRef.value?.focus())
    return false
  }

  // // 如果输入的是纯数字，则按手机号格式校验
  // if (/^\d+$/.test(formData.value.account)) {
  //   if (!/^1[3456789]\d{9}$/.test(formData.value.account)) {
  //     accountError.value = '请输入正确的手机号'
  //     nextTick(() => accountInputRef.value?.focus())
  //     return false
  //   }
  // }

  accountError.value = ''
  return true
}


const validatePassword = () => {
  if (!formData.value.password) {
    passwordError.value = t('login.passwordRequired')
    nextTick(() => passwordInputRef.value?.focus())
    return false
  }
  if (formData.value.password.length < 2 || formData.value.password.length > 20) {
    passwordError.value = t('login.passwordLength')
    nextTick(() => passwordInputRef.value?.focus())
    return false
  }
  passwordError.value = ''
  return true
}


const validateCaptcha = () => {
  if (showCaptcha.value && !formData.value.captcha) {
    captchaError.value = '请输入图形验证码'
    nextTick(() => captchaInputRef.value?.focus())
    return false
  }
  captchaError.value = ''
  return true
}

const onCaptchaInput = (e: Event) => {
  if (isCaptchaComposing.value) {
    return
  }
  const target = e.target as HTMLInputElement
  formData.value.captcha = target?.value ?? ''
  captchaError.value = ''
}

const onCaptchaCompositionStart = () => {
  isCaptchaComposing.value = true
}

const onCaptchaCompositionEnd = (e: Event) => {
  isCaptchaComposing.value = false
  onCaptchaInput(e)
}

const onCaptchaBlur = (e: Event) => {
  isCaptchaComposing.value = false
  const target = e.target as HTMLInputElement
  formData.value.captcha = target?.value ?? ''
  captchaError.value = ''
}


/**
 * 初始化记住账号功能
 */
const initRememberAccount = () => {
  const loginHistory = getLoginHistory();
    if (!loginHistory || !Array.isArray(loginHistory) || loginHistory.length === 0) {
    const savedAccount = getKeyStr('lgusr');
    if (savedAccount) {
      formData.value.account = savedAccount;
    }
    if (savedAccount && agreeUsers.value[savedAccount]) {
      rememberAccount.value = true;
    }

    return;
  }

  if(loginHistory[0].agreeTerms){
      // agreeTerms.value = true;
      props.updateAgreeTerms(true)
    }
  fillUserInfo(loginHistory[0]);
};

/**
 *  填充用户信息到表单
 */
const fillUserInfo = (user: any) => {
  formData.value.account = user.account;
  const agreeStatus = user.agreeStatus || agreeUsers.value[user.account] || false;
  nextTick(() => {
    rememberAccount.value = agreeStatus;
  });
};
/**
 * 保存记住的账号
 */
// const saveRememberAccount = () => {
//   if (rememberAccount.value) {
//     setKey('lgusr', formData.value.account.trim())
//     setKey('rememberAccount', 'true')
//   } else {
//     removeKey('lgusr')
//     removeKey('rememberAccount')
//   }
// }

/**
 * 处理记住账号复选框变化
 */
// const handleRememberChange = () => {
//   if (!rememberAccount.value) {
//     removeKey('lgusr')
//     removeKey('rememberAccount')
//   }
// }

/**
 * 服务器配置完成回调
 */
const handleServerConfigured = () => {
  console.log('服务器配置完成')
  // 重新加载页面以应用新的服务器配置
  window.location.reload()
}

/**
 * 提交登录表单
 */
const handleSubmit = async () => {
  // 检查用户协议
  // if (detectPlatform() != 1) {
    if (!props.agreeTerms) {
      toast('请阅读并同意用户协议和隐私政策', 3)
      return
    }
  // }

  if (!validateAccount() || !validatePassword() || !validateCaptcha()) {
    return
  }


  loading.value = true
await openMLoading();
  const submitData: any = {
    account: formData.value.account.trim(),
    password: formData.value.password.trim(),
    orgid: 'luatos',
    //
    params: 'a38940b6e24675fbabbf17b9043a667cb221f616b1a772565445d2ded0ceb6bb',
  }
  if (detectPlatform() == 1) {
    submitData.clientId = 'luatWx'
  } else if (detectPlatform() == 2) {
    submitData.clientId = 'luatOsApp'
  }else{
    // 移动版默认走wx方便调试
    submitData.clientId = 'luatWx'
  }

  // 如果显示了验证码，则添加验证码参数
  if (showCaptcha.value) {
    submitData.captchaId = captchaId.value
    submitData.captchaCode = formData.value.captcha
  }
  setKey('login',JSON.stringify({ctxt: "iot",path: "/tenantlogin"}))
  Promise.race([userStore.externalLogin(submitData), new Promise((_, r) => setTimeout(r, 5000, '请求超时'))])
    .then(async () => {
      loginFailCount.value = 0
      showCaptcha.value = false
      formData.value.captcha = ''

      // saveRememberAccount()
      const finalLang = i18n.global?.locale?.value ?? 'zh-cn';
      const loginUnid = userStore.unid;

      if (loginUnid) {
        const history = Array.isArray(getsubKey('mobile', 'user_info')) ? [...getsubKey('mobile', 'user_info')] : [];
        const userIdx = history.findIndex((r: any) => r.unid === loginUnid);
        const prevRecord = userIdx >= 0 ? history[userIdx] : { unid: loginUnid };
        const nextRecord: any = {
          ...prevRecord,
          account: formData.value.account.trim(),
          loginTime: new Date().toISOString(),
          lang: finalLang,
          agreeStatus: rememberAccount.value,
          unid: loginUnid,
          agreeTerms:props.agreeTerms
        };
        if (userIdx >= 0) history.splice(userIdx, 1);
        history.unshift(nextRecord);
        updateKeyJson('mobile', 'user_info', history.slice(0, 10));
      }

      if (rememberAccount.value && submitData.account) {
        agreeUsers.value[submitData.account] = true;
      }





      try {
       
        await userStore.firstMenuFun()
        await addDynamicRoutes(true)
        await serviceConfig(userStore.ctxt)
        initTrueTheme()

        if (isMobile()) {
          settingStore['layout'] = 'drawer'
          settingStore['isUseTabsRouter'] = false
          settingStore['isSidebarCompact'] = false
        }

        await briefStore.getIamBrief()
        await briefStore.getServiceBrief()

        const targetRoute = getJumpviewRouteLocation(router, route.query)
        console.log('[LuatosLogin] 登录后目标路由:', targetRoute || '/')
        router.replace(targetRoute || '/')
      } catch (e: any) {
        console.error('登录后初始化失败', e)
        toast(e?.msg || String(e), 3)
      }
    })
    .catch((err: any) => {
      // 登录失败处理
      loginFailCount.value++

      // 登录失败后显示图形验证码（验证码已提前加载）
      if (!showCaptcha.value) {
        showCaptcha.value = true
      } else {
        // 如果已经显示验证码，刷新验证码
        refreshCaptcha()
        formData.value.captcha = ''
      }

      if (err === '请求超时') {
        toast(err, 3)
        return
      }
      toast(err, 3)
      console.log('登录失败:', err)
    })
    .finally(() => {
      loading.value = false
      closeMLoading();
    })
}

const serviceConfig = async (ctxt: string) => {
  if (!ctxt) {
    return
  }
  try {
    await userStore.loadServiceConfig(ctxt)
  } catch (e: any) {
    if (e?.response && e?.response?.status > 0) {
      toast(`${e?.config?.url}：${e?.response?.status}-${e?.response?.statusText}`, 3)
    } else {
      toast(e?.message || String(e), 3)
    }
    throw e
  }
}

function initTrueTheme() {
  try {
    settingStore.updateConfig(userStore['erpConfig'])
  } catch (e) {
    console.log('初始化合并异常', e)
  }
}

// 生命周期
onMounted(() => {
  initRememberAccount()

  // 提前加载图形验证码（不显示，但数据已准备好）
  refreshCaptcha()

  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardDidHide', () => {
      syncInputValues()
    })
  }
})

onUnmounted(() => {
  if (Capacitor.isNativePlatform()) {
    Keyboard.removeAllListeners()
  }
})
</script>

<style scoped lang="scss">
.page-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.35);
}

.page-loading-mask :deep(view) {
  display: block;
}

.page-loading-mask :deep(.wrapper) {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.page-loading-mask :deep(.spinner) {
  margin: 0;
}

$primary-color-login-text: rgba(57, 140, 254, 1);

// ===== 科技感配色 =====
$primary-gradient:  rgba(57, 140, 254, 1);
// $primary-gradient: $primary-color-login-text;
 //$accent-color: ;
$accent-color: $primary-color-login-text;
$light-accent: rgba(57, 140, 254, 0.1);
$text-secondary: #8a94a6;
$border-light: rgba(57, 140, 254, 0.2);

// ===== 间距变量 =====
$spacing-xs: 0.8rem;
$spacing-sm: 0.85rem;
$spacing-md: 1.25rem;
$spacing-lg: 1.5rem;
$spacing-xl: 1.875rem;
$spacing-2xl: 2.5rem;
$spacing-3xl: 3rem;

// ===== 圆角变量 =====
$radius-sm: 4px;
$radius-md: 8px;
$radius-lg: 14px;
$radius-xl: 16px;
$radius-full: 54px;
$radius-circle: 50%;

// ===== 阴影变量 =====
$shadow-xs: 0 2px 8px rgba(0, 0, 0, 0.02);
$shadow-sm: 0 2px 10px rgba(57, 140, 254, 0.3);
$shadow-md: 0 4px 16px rgba(57, 140, 254, 0.3);
$shadow-lg: 0 6px 20px rgba(57, 140, 254, 0.45);
$shadow-hover: 0 4px 16px rgba($accent-color, 0.08);
$shadow-focus: 0 0 0 4px rgba($accent-color, 0.1),
  0 6px 20px rgba($accent-color, 0.12);
$shadow-modal: 0 10px 40px rgba(0, 0, 0, 0.2);

// ===== 字体变量 =====
$font-size-sm: 0.8rem;
$font-size-base: 0.95rem;
$font-size-lg: 1rem;
$font-size-xl: 1.1rem;
$font-size-2xl: 28px;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 900;

// ===== 动画变量 =====
$transition-fast: 0.15s ease;
$transition-base: 0.3s ease;
$transition-md: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
$transition-lg: 0.4s cubic-bezier(0.4, 0, 0.2, 1);
$transition-xl: 0.5s ease;

.login {
  width: 100%;

  // ===== 标题动画样式 =====
  h2 {
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    letter-spacing: 0.1em;
  }

  // 新增登录标题类样式
  .login-title {
    font-weight: $font-weight-bold;
    // margin-bottom: 1rem;
  }

  // ===== 表单容器增强 =====
  :deep(.form) {
    padding: $spacing-lg 0;
    position: relative;
    width: 80%;

    // 输入框组 - 现代卡片式设计
    .input-group {
      position: relative;
      margin-top: $spacing-xl;
      background: #fafbfc;
      border: 2px solid #e8ecf2 !important;
      border-radius: $radius-full;
      padding: 14px 54px;
      transition: all $transition-md;
      box-shadow: $shadow-xs;
      outline: none;

      // 悬浮效果
      &:hover {
        border-color: rgba($accent-color, 0.4);
        background: #fff;
        box-shadow: $shadow-hover;
      }

      // 聚焦效果
      &:focus-within {
        border-color: $accent-color !important;
        background: #fff;
        box-shadow: $shadow-focus;

        .lebal-tilte {
          color: $accent-color !important;
          font-weight: $font-weight-semibold;
        }

        .svg {
          opacity: 1;
          color: $accent-color;
          // transform: scale(1.15);
        }
      }

      // 错误状态样式
      &.has-error {
        border-color: #f56c6c !important;
        background: #fef0f0;

        &:hover {
          border-color: #f56c6c !important;
          background: #fef0f0;
        }

        &:focus-within {
          border-color: #f56c6c !important;
          background: #fef0f0;
          box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.1);

          .svg {
            color: #f56c6c;
          }
        }

        .svg {
          color: #f56c6c;
        }
      }

      // 错误提示文字（绝对定位，不占据高度）
      .error-message {
        position: absolute;
        left: 54px;
        right: 54px;
        top: 100%;
        margin-top: 8px;
        font-size: 24px;
        color: #f56c6c;
        line-height: 1.2;
        font-weight: 500;
        animation: slideDown 0.3s ease;
        z-index: 10;
      }

      // 输入框本身
      .main-input {
        padding: 0.5rem 0.5rem 0.5rem 0.25rem;
        border: none;
        background: transparent !important;
        font-size: $font-size-lg;
        font-weight: $font-weight-medium;
        color: #2d3748;
        width: 100%;
        transition: all $transition-base;

        &::placeholder {
          color: $text-secondary;
        }

        &:focus {
          outline: none;
        }
      }

      // 浮动标签
      .lebal-tilte {
        position: absolute;
        left: 0.85rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: $font-size-base;
        color: #9ca3af;
        font-weight: $font-weight-medium;
        pointer-events: none;
        transition: all $transition-md;
        background: transparent;
        padding: 0 0.25rem;
      }

      // 右侧图标
      .svg {
        position: absolute;
        right: 0.9rem;
        top: 45%;
        transform: translateY(-50%);
        font-size: 1.5rem;
        // color: #b0b8c4;
        color:$primary-color-login-text;
        opacity: 0.7;
        transition: all $transition-md;
        
        // 密码切换图标可点击
        &.password-toggle {
          cursor: pointer;
          user-select: none;
          
          &:hover {
            opacity: 1;
            transform: translateY(-50%) scale(1.1);
          }
          
          &:active {
            transform: translateY(-50%) scale(0.95);
          }
        }
      }

      // 隐藏原有的highlight-span
      .highlight-span {
        display: none;
      }
    }

    // 新增密码组类样式
    .password-group {
      margin-top: $spacing-lg;
    }

    // 图形验证码行：input 占左侧，验证码图片在右侧
    .captcha-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .captcha-img {
        flex-shrink: 0;
        width: 130px;
        height: 100%;
        cursor: pointer;
        border-left: 2px solid #e8ecf2;
        display: flex;
        margin-right: 20px;
        align-items: center;
        justify-content: center;
        border-radius: 0 $radius-full $radius-full 0;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .captcha-placeholder {
          font-family: 'Courier New', monospace;
          font-size: 1.15rem;
          font-weight: $font-weight-bold;
          letter-spacing: 0.25em;
          // 彩色渐变文字
          background: linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // 背景：网格噪点质感
          background-color: #eef5ff;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          position: relative;
          // 模拟噪点纹理
          background-image:
            linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%),
            repeating-linear-gradient(45deg,
              rgba(57, 123, 213, 0.04) 0px,
              rgba(57, 123, 213, 0.04) 1px,
              transparent 1px,
              transparent 8px);
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.6), -1px -1px 2px rgba(0, 0, 0, 0.08);
        }
      }
    }

    // 第二个输入框间距调整
    .input-group+.input-group,
    .input-group[style*='margin-top'] {
      margin-top: $spacing-xl !important;
    }

    // 密码操作区域
    .group-password {
      margin-top: $spacing-lg;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      // 圆形 checkbox 样式
      .remember-checkbox {
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;

        input[type='checkbox'] {
          display: none;
        }

        .checkmark {
          width: 24px;
          height: 24px;
          display: inline-block;
          min-width: 24px;
          border: 2px solid #d1d5db;
          border-radius: $radius-sm;
          margin-right: 8px;
          position: relative;
          transition: all $transition-base;
          background: #fff;

          &::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            font-size: 12px;
            font-weight: bold;
            color: #fff;
            transform: translate(-50%, -50%) scale(0);
            transition: transform 0.2s ease;
          }
        }

        input[type='checkbox']:checked+.checkmark {
          border-color: $accent-color;
          background: $accent-color;

          &::after {
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .label-text {
          font-size: 0.85rem;
          color: $text-secondary;
          font-weight: $font-weight-medium;

          // line-height: 1.5;
          .link {
            color: $accent-color;
            font-weight: 600;
            cursor: pointer;
          }
        }
      }

      .forgot-password {
        font-size: 28px;
        color: $accent-color;
        font-weight: $font-weight-medium;
        cursor: pointer;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }
      }
    }



    // 登录按钮增强
    .Signup-Btn {

      // 从内联样式移过来的margin-top属性
      &.login-button {
        margin-top: $spacing-xl; // 转换30px为rem
      }

      background: $primary-gradient;
      border: none;
      padding: 0 $spacing-lg;
      height: 88px;
      min-height: 88px;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      box-shadow: $shadow-md;
      transition: all $transition-lg;
      position: relative;
      overflow: hidden;
      border-radius: $radius-full;
      color: #fff;
      width: 100%;
      user-select: none;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left $transition-xl;
      }

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: $shadow-lg;

        &::before {
          left: 100%;
        }
      }

      &:active:not(:disabled) {
        transform: translateY(0) scale(0.98);
        box-shadow: $shadow-sm;
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }

      // 按钮文本样式
      .button-text {
        margin-left: 10px;
      }
    }

    // 底部链接：服务设置和注册账号
    .register-link {
      margin-top: $spacing-md;
      text-align: center;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 20px;

      .link-text {
        font-size: 28px;
        color: $accent-color;
        font-weight: $font-weight-medium;
        cursor: pointer;
        transition: opacity $transition-base;
        user-select: none;

        &:hover {
          opacity: 0.8;
        }

        &.server-setting {
          color: $text-secondary;
        }
      }
    }

  }
}



// ===== 其他登录方式 =====
.login-other {
  width: 80%;
  margin-top: $spacing-2xl;
  color: $text-secondary;

  &>h5 {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: $spacing-lg;
    color: $text-secondary;
    font-weight: $font-weight-medium;
    font-size: $font-size-2xl;
    text-align: center;
    white-space: nowrap;
    letter-spacing: 0.05em;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: linear-gradient(90deg, transparent, $border-light, transparent);
    }

    &::before {
      margin-right: $spacing-xs;
      background: linear-gradient(90deg, transparent, $border-light);
    }

    &::after {
      margin-left: $spacing-xs;
      background: linear-gradient(90deg, $border-light, transparent);
    }
  }

  .other-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: $spacing-3xl;
    perspective: 500px;

    &>.icon {
      cursor: pointer;
      padding: $spacing-sm;
      border-radius: $radius-circle;
      transition: all $transition-fast;
      position: relative;

      background-image: linear-gradient(to top, #d8d9db 0%, #fff 80%, #fdfdfd 100%);
      border-radius: 75px;
      border: 1px solid #ddd;
      text-shadow: 0 1px #fff;

      &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 60%;
        height: 6px;
        background: radial-gradient(ellipse, rgba(0, 0, 0, 0.15) 0%, transparent 70%);
        border-radius: $radius-circle;
        transition: all $transition-fast;
      }

      &:hover {
        transform: scale(1.1);
        box-shadow:
          8px 12px 20px rgba(57, 140, 254, 0.25),
          -4px -4px 12px rgba(255, 255, 255, 1),
          inset 1px 1px 3px rgba(255, 255, 255, 0.9),
          inset -1px -1px 3px rgba(57, 140, 254, 0.1);

        &::after {
          width: 50%;
          opacity: 0.8;
          bottom: -12px;
        }
      }

      &:active {
        transform: translateY(-2px) scale(1.05);
        box-shadow:
          4px 4px 8px rgba(0, 0, 0, 0.12),
          -2px -2px 6px rgba(255, 255, 255, 0.8),
          inset 2px 2px 4px rgba(0, 0, 0, 0.08),
          inset -1px -1px 2px rgba(255, 255, 255, 0.6);
      }
    }
  }
}

// ===== 多租户弹窗样式 =====
.tenant-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .tenant-content {
    background: #fff;
    border-radius: $radius-xl;
    padding: $spacing-lg;
    min-width: 280px;
    max-width: 90%;
    box-shadow: $shadow-modal;

    h3 {
      margin: 0 0 $spacing-xs;
      font-size: $font-size-xl;
      color: #2d3748;
      text-align: center;
    }

    .tenant-list {
      max-height: 300px;
      overflow-y: auto;

      .tenant-item {
        padding: 0.75rem $spacing-xs;
        border-radius: $radius-md;
        cursor: pointer;
        transition: background $transition-base;

        &:hover {
          background: rgba($accent-color, 0.1);
        }

        &:active {
          background: rgba($accent-color, 0.2);
        }
      }
    }
  }
}


// ===== 关键帧动画 =====
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes lineExpand {
  from {
    width: 0;
  }

  to {
    width: 40px;
  }
}

// ===== 输入框焦点波纹效果 =====
@keyframes focusRipple {
  0% {
    transform: scale(0);
    opacity: 0.5;
  }

  100% {
    transform: scale(2);
    opacity: 0;
  }
}

// ===== 浮动动画（装饰用）=====
@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

// ===== 错误提示滑入动画 =====
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
